"use client";
import * as React from "react";
import { useState, useEffect } from "react";

type Keys = keyof typeof INITIAL_VALUES.samlConnection;
type Values = (typeof INITIAL_VALUES.samlConnection)[Keys];
import ToggleConnectionStatus from "../../ToggleConnectionStatus/index";
import type {
  EditSAMLConnectionProps,
  SAMLSSOConnection,
  SAMLSSORecord,
  SAMLFormState,
} from "../../types";
import { saveConnection, deleteConnection } from "../../utils";
import defaultClasses from "./index.module.css";
import cssClassAssembler from "../../../utils/cssClassAssembler";
import SecretInputFormControl from "../../../../shared/inputs/SecretInputFormControl/index";
import Card from "../../../../shared/Card/index";
import Button from "../../../../shared/Button/index";
import Spacer from "../../../../shared/Spacer/index";
import Separator from "../../../../shared/Separator/index";
import ConfirmationPrompt from "../../../../shared/ConfirmationPrompt/index";
import Checkbox from "../../../../shared/Checkbox/index";
import InputField from "../../../../shared/inputs/InputField/index";
import TextArea from "../../../../shared/inputs/TextArea/index";
import { InputWithCopyButton } from "../../../../shared";
import LoadingContainer from "../../../../shared/LoadingContainer/index";
import { ApiResponse, sendHTTPRequest } from "../../../../shared/http";
import ItemList from "../../../../shared/inputs/ItemList/index";
const DEFAULT_VALUES = {
  variant: "basic",
} satisfies Partial<EditSAMLConnectionProps>;
const INITIAL_VALUES = {
  samlConnection: {
    name: "",
    label: "",
    tenant: "",
    product: "",
    clientID: "",
    clientSecret: "",
    description: "",
    redirectUrl: [""],
    defaultRedirectUrl: "",
    rawMetadata: "",
    metadataUrl: "",
    forceAuthn: false as boolean,
    sortOrder: "" as unknown as string | number,
    acsUrlOverride: "",
  } as SAMLFormState,
};

function EditSAMLConnection(props: EditSAMLConnectionProps) {
  const [samlConnection, setSamlConnection] = useState(
    () => INITIAL_VALUES.samlConnection
  );

  const [isConnectionLoading, setIsConnectionLoading] = useState(() => true);

  const [isSaving, setIsSaving] = useState(() => false);

  const [showDelConfirmation, setShowDelConfirmation] = useState(() => false);

  function toggleDelConfirmation() {
    setShowDelConfirmation(!showDelConfirmation);
  }

  function formVariant() {
    return props.variant || DEFAULT_VALUES.variant;
  }

  function classes() {
    return {
      formDiv: cssClassAssembler(
        props.classNames?.formDiv,
        defaultClasses.formDiv
      ),
      label: cssClassAssembler(props.classNames?.label, defaultClasses.label),
      inputField: {
        label: props.classNames?.label,
        input: props.classNames?.input,
        container: props.classNames?.fieldContainer,
      },
      textarea: {
        label: props.classNames?.label,
        textarea: props.classNames?.textarea,
        container: props.classNames?.fieldContainer,
      },
      section: cssClassAssembler(
        props.classNames?.section,
        defaultClasses.section
      ),
    };
  }

  function isExcluded(fieldName: keyof SAMLSSOConnection) {
    return !!(props.excludeFields as (keyof SAMLSSOConnection)[])?.includes(
      fieldName
    );
  }

  function updateConnection(data: Partial<SAMLSSOConnection>) {
    return {
      ...samlConnection,
      ...data,
    };
  }

  function handleChange(event: Event) {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
    const id = target.id as Keys;
    const targetValue =
      id !== "forceAuthn" ? target.value : (target as HTMLInputElement).checked;
    setSamlConnection(
      updateConnection({
        [id]: targetValue,
      })
    );
  }

  function handleItemListUpdate(fieldName: string, listValue: string[]) {
    setSamlConnection(
      updateConnection({
        [fieldName]: listValue,
      })
    );
  }

  function saveSSOConnection(event: Event) {
    event.preventDefault();
    const { sortOrder, ...rest } = samlConnection;
    // pass sortOrder only if set to non-empty string
    const payload =
      sortOrder === ""
        ? rest
        : {
            ...samlConnection,
            sortOrder: +sortOrder!,
          };
    setIsSaving(true);
    saveConnection<undefined>({
      url: props.urls.patch,
      isEditView: true,
      formObj: payload,
      connectionIsSAML: true,
      callback: async (data) => {
        setIsSaving(false);
        if (data?.error) {
          typeof props.errorCallback === "function" &&
            props.errorCallback(data.error.message);
        } else {
          typeof props.successCallback === "function" &&
            props.successCallback({
              operation: "UPDATE",
              connection: payload,
              connectionIsSAML: true,
            });
        }
      },
    });
  }

  function deleteSSOConnection(event: Event) {
    event.preventDefault();
    deleteConnection({
      url: props.urls.delete,
      clientId: samlConnection.clientID!,
      clientSecret: samlConnection.clientSecret!,
      callback: async (data: ApiResponse<undefined>) => {
        if (data?.error) {
          typeof props.errorCallback === "function" &&
            props.errorCallback(data.error.message);
        } else {
          typeof props.successCallback === "function" &&
            props.successCallback({
              operation: "DELETE",
              connectionIsSAML: true,
            });
        }
      },
    });
  }

  function shouldDisplayHeader() {
    if (props.displayHeader !== undefined) {
      return props.displayHeader;
    }
    return true;
  }

  function shouldDisplayInfoCard() {
    if (props.displayInfo !== undefined) {
      return props.displayInfo;
    }
    return true;
  }

  function connectionFetchUrl() {
    return props.urls.get;
  }

  useEffect(() => {
    async function getConnection(url: string) {
      const data = await sendHTTPRequest<SAMLSSORecord[]>(url);
      setIsConnectionLoading(false);
      if (data && typeof data === "object") {
        if ("error" in data && data.error) {
          typeof props.errorCallback === "function" &&
            props.errorCallback(data.error.message);
        } else if (Array.isArray(data)) {
          const _connection = data[0];
          if (_connection) {
            setSamlConnection({
              ..._connection,
              name: _connection.name || "",
              label: _connection.label || "",
              tenant: _connection.tenant || "",
              product: _connection.product || "",
              clientID: _connection.clientID,
              clientSecret: _connection.clientSecret,
              description: _connection.description || "",
              redirectUrl: _connection.redirectUrl,
              defaultRedirectUrl: _connection.defaultRedirectUrl,
              rawMetadata: _connection.rawMetadata || "",
              metadataUrl: _connection.metadataUrl || "",
              forceAuthn: _connection.forceAuthn === true,
              sortOrder: _connection.sortOrder ?? "",
            });
          }
        }
      }
    }
    getConnection(connectionFetchUrl());
  }, [connectionFetchUrl()]);

  return (
    <LoadingContainer isBusy={isConnectionLoading}>
      <div className={classes().formDiv}>
        <div className={defaultClasses.headingContainer}>
          {shouldDisplayHeader() ? (
            <h5 className={defaultClasses.h5}>Edit SSO Connection</h5>
          ) : null}
          <ToggleConnectionStatus
            connection={samlConnection}
            urls={{
              patch: props.urls.patch,
            }}
            classNames={{
              confirmationPrompt: {
                button: {
                  ctoa: `${
                    props.classNames?.confirmationPrompt?.button?.ctoa
                  } ${
                    samlConnection.deactivated
                      ? props.classNames?.button?.ctoa
                      : props.classNames?.button?.destructive
                  }`.trim(),
                  cancel: props.classNames?.confirmationPrompt?.button?.cancel,
                },
              },
            }}
            errorCallback={props.errorCallback}
            successCallback={props.successCallback}
          />
        </div>
        <div>
          <form method="post" onSubmit={(event) => saveSSOConnection(event)}>
            {formVariant() === "advanced" ? (
              <>
                {!isExcluded("name") ? (
                  <>
                    <InputField
                      label="Connection name (Optional)"
                      id="name"
                      placeholder="MyApp"
                      classNames={classes().inputField}
                      required={false}
                      value={samlConnection.name!}
                      handleInputChange={handleChange}
                    />
                    <Spacer y={6} />
                  </>
                ) : null}
                {!isExcluded("label") ? (
                  <>
                    <InputField
                      label="Connection label (Optional)"
                      id="label"
                      placeholder="An internal label to identify the connection"
                      classNames={classes().inputField}
                      required={false}
                      value={samlConnection.label!}
                      handleInputChange={handleChange}
                    />
                    <Spacer y={6} />
                  </>
                ) : null}
                {!isExcluded("description") ? (
                  <>
                    <InputField
                      label="Description (Optional)"
                      id="description"
                      placeholder="A short description not more than 100 characters"
                      classNames={classes().inputField}
                      required={false}
                      maxLength={100}
                      value={samlConnection.description!}
                      handleInputChange={handleChange}
                    />
                    <Spacer y={6} />
                  </>
                ) : null}
                {!isExcluded("redirectUrl") ? (
                  <>
                    <ItemList
                      inputType="url"
                      label="Allowed redirect URLs"
                      fieldName="redirectUrl"
                      currentlist={samlConnection.redirectUrl}
                      handleItemListUpdate={handleItemListUpdate}
                      classNames={classes().inputField}
                    />
                    <div id="redirectUrl-hint" className={defaultClasses.hint}>
                      URL(s) to redirect the user to after login. Only the URLs
                      in this list are allowed in the OAuth flow.
                    </div>
                    <Spacer y={6} />
                  </>
                ) : null}
                {!isExcluded("defaultRedirectUrl") ? (
                  <>
                    <InputField
                      label="Default redirect URL"
                      id="defaultRedirectUrl"
                      aria-describedby="defaultRedirectUrl-hint"
                      placeholder="http://localhost:3366/login/saml"
                      type="url"
                      required
                      classNames={classes().inputField}
                      value={samlConnection.defaultRedirectUrl!}
                      handleInputChange={handleChange}
                    />
                    <div
                      id="defaultRedirectUrl-hint"
                      className={defaultClasses.hint}
                    >
                      URL to redirect the user to after an IdP initiated SAML
                      login.
                    </div>
                    <Spacer y={6} />
                  </>
                ) : null}
              </>
            ) : null}
            <TextArea
              label="Raw IdP XML"
              id="rawMetadata"
              aria-describedby="xml-metadata-hint"
              placeholder="Paste the raw XML here"
              classNames={classes().textarea}
              required={
                !samlConnection.idpMetadata && samlConnection.metadataUrl === ""
              }
              value={samlConnection.rawMetadata!}
              handleInputChange={handleChange}
            />
            <div id="xml-metadata-hint" className={defaultClasses.hint}>
              Paste the raw XML metadata obtained from SAML provider or enter
              the metadata URL below.
            </div>
            <Spacer y={6} />
            <Separator text="OR" />
            <Spacer y={6} />
            <InputField
              label="Metadata URL"
              id="metadataUrl"
              type="url"
              placeholder="Paste the Metadata URL here"
              classNames={classes().inputField}
              required={
                !samlConnection.idpMetadata && samlConnection.rawMetadata === ""
              }
              value={samlConnection.metadataUrl!}
              handleInputChange={handleChange}
            />
            <Spacer y={6} />
            {formVariant() === "advanced" ? (
              <>
                {!isExcluded("forceAuthn") ? (
                  <Checkbox
                    label="Force Authentication"
                    name="forceAuthn"
                    id="forceAuthn"
                    checked={samlConnection.forceAuthn === true}
                    handleChange={handleChange}
                  />
                ) : null}
              </>
            ) : null}
            <Spacer y={6} />
            {formVariant() === "advanced" ? (
              <>
                {!isExcluded("sortOrder") ? (
                  <>
                    <InputField
                      label="Sort Order"
                      id="sortOrder"
                      type="number"
                      min="0"
                      placeholder="10"
                      classNames={classes().inputField}
                      value={samlConnection.sortOrder as string}
                      handleInputChange={handleChange}
                    />
                    <div id="sortOrder-hint" className={defaultClasses.hint}>
                      Connections will be sorted (in a listing view like IdP
                      Selection) using this setting. Higher values will be
                      displayed first.
                    </div>
                  </>
                ) : null}
              </>
            ) : null}
            <Spacer y={6} />
            {formVariant() === "advanced" ? (
              <>
                {!isExcluded("acsUrlOverride") ? (
                  <InputField
                    label="ACS Url Override"
                    id="acsUrlOverride"
                    type="url"
                    placeholder="https://yourcompany.com/app/saml/acs"
                    classNames={classes().inputField}
                    value={samlConnection.acsUrlOverride as string}
                    handleInputChange={handleChange}
                  />
                ) : null}
              </>
            ) : null}
            <Spacer y={6} />
            <div className={defaultClasses.formAction}>
              {typeof props.cancelCallback === "function" ? (
                <Button
                  type="button"
                  name="Cancel"
                  variant="outline"
                  handleClick={props.cancelCallback}
                  classNames={props.classNames?.button?.cancel}
                />
              ) : null}
              <Button
                type="submit"
                name="Save"
                classNames={props.classNames?.button?.ctoa}
                isLoading={isSaving}
              />
            </div>
            {shouldDisplayInfoCard() ? (
              <Card
                title="Connection info"
                variant="info"
                arrangement="vertical"
              >
                <div className={defaultClasses.info}>
                  {formVariant() === "advanced" ? (
                    <>
                      {!isExcluded("tenant") ? (
                        <>
                          <InputField
                            label="Tenant"
                            id="tenant"
                            placeholder="acme.com"
                            classNames={classes().inputField}
                            required
                            readOnly
                            value={samlConnection.tenant!}
                          />
                          <Spacer y={6} />
                        </>
                      ) : null}
                      {!isExcluded("product") ? (
                        <>
                          <InputField
                            label="Product"
                            id="product"
                            placeholder="demo"
                            classNames={classes().inputField}
                            required
                            readOnly
                            value={samlConnection.product!}
                          />
                          <Spacer y={6} />
                        </>
                      ) : null}
                    </>
                  ) : null}
                  {props.displayIdpMetadata === true ? (
                    <>
                      <div className={defaultClasses.container}>
                        <span className={classes().label}>IdP Metadata</span>
                        <Spacer y={2} />
                        <pre aria-readonly className={defaultClasses.pre}>
                          {JSON.stringify(samlConnection.idpMetadata, null, 2)}
                        </pre>
                      </div>
                      <Spacer y={6} />
                    </>
                  ) : null}
                  <div className={defaultClasses.container}>
                    <InputField
                      id="idp_cert_validity"
                      label="IdP Certificate Validity"
                      readOnly
                      classNames={classes().inputField}
                      value={samlConnection.idpMetadata?.validTo!}
                    />
                  </div>
                  <Spacer y={6} />
                  <InputWithCopyButton
                    label="Client ID"
                    text={samlConnection.clientID || ""}
                    classNames={classes().inputField}
                    copyDoneCallback={props.successCallback}
                  />
                  <Spacer y={6} />
                  <SecretInputFormControl
                    label="Client Secret"
                    id="clientSecret"
                    classNames={{
                      input: props.classNames?.secretInput,
                    }}
                    value={samlConnection.clientSecret!}
                    readOnly
                    required
                    copyDoneCallback={props.successCallback}
                    handleChange={handleChange}
                  />
                  <Spacer y={6} />
                </div>
              </Card>
            ) : null}
            <Spacer y={4} />
            {samlConnection?.clientID && samlConnection.clientSecret ? (
              <section className={classes().section}>
                <div className={defaultClasses.info}>
                  <h6 className={defaultClasses.sectionHeading}>
                    Delete this connection
                  </h6>
                  <p className={defaultClasses.sectionPara}>
                    All your apps using this connection will stop working.
                  </p>
                </div>
                {!showDelConfirmation ? (
                  <Button
                    name="Delete"
                    variant="destructive"
                    type="button"
                    handleClick={toggleDelConfirmation}
                    classNames={props.classNames?.button?.destructive}
                  />
                ) : null}
                {showDelConfirmation ? (
                  <ConfirmationPrompt
                    ctoaVariant="destructive"
                    promptMessage=" Are you sure you want to delete the Connection? This action cannot be undone and will permanently delete the Connection."
                    classNames={{
                      button: {
                        ctoa: `${props.classNames?.button?.destructive} ${props.classNames?.confirmationPrompt?.button?.ctoa}`.trim(),
                        cancel:
                          props.classNames?.confirmationPrompt?.button?.cancel,
                      },
                    }}
                    cancelCallback={toggleDelConfirmation}
                    confirmationCallback={deleteSSOConnection}
                  />
                ) : null}
              </section>
            ) : null}
          </form>
        </div>
      </div>
    </LoadingContainer>
  );
}

export default EditSAMLConnection;
